import { createReducer, createAction } from '@reduxjs/toolkit';
import { backendCall, createApiCall } from '@reducers/utils';
import { getDemoBatchSearch, getDemoOperationsSearch } from '@components/tours/helpers/demoDataRegistry';
import type { AppThunk } from '@store/types';
import type {
  SearchState,
  SearchSuggestionRequest,
  SearchWithFilterParams,
  GlobalSearchResponse,
  ProductionOrderSearchResponse,
  PartSearchResponse,
  ProductionOperationSearchResponse,
  ProductionBatchSearchResponse,
  NcFileSearchResponse,
  ToolSearchResponse,
  DocumentSearchResponse,
  RecipeSearchResponse,
  RecipeOperationSearchResponse,
  MachineEventSearchResponse,
  MachineEventCycleSearchResponse,
  SuggestionResponse,
  Document,
  BatchByIdParams,
  BatchByMachineParams,
} from './types';

// Re-export types for consumers
export type {
  SearchState,
  GlobalSearchResponse,
  ProductionOrderSearchResponse,
  PartSearchResponse,
  ProductionOperationSearchResponse,
  ProductionBatchSearchResponse,
  NcFileSearchResponse,
  ToolSearchResponse,
  DocumentSearchResponse,
  RecipeSearchResponse,
  RecipeOperationSearchResponse,
  MachineEventSearchResponse,
  MachineEventCycleSearchResponse,
  SuggestionResponse,
} from './types';

// === Initial State ===

const initState: SearchState = {
  shouldRefetchData: false,
  statStorage: {},
  statContextStorage: {},
  totalSearchResult: null,
};

// === Actions ===

export const clear = createAction('clear');
export const close = createAction('search/close');
export const setStatStorage = createAction<{ name: string; value: number }>('search/setStatStorage');
export const setStatContextStorage = createAction<Record<string, unknown>>('search/statContextStorage');
export const setShouldRefetchDataAction = createAction<boolean>('setShouldRefetchDataAction');
export const setTotalSearchResultAction = createAction<number | null>('search/setTotalSearchResultAction');

// === Thunks ===

export const setShouldRefetchData = (state: boolean): AppThunk => (dispatch) => {
  dispatch(setShouldRefetchDataAction(state));
};

export const globalSuggestion = (data: SearchSuggestionRequest, signal?: AbortSignal): AppThunk<Promise<SuggestionResponse | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.globalSuggestion, { params: data, signal })
    )
  );
};

export const partsSuggestion = (data: SearchSuggestionRequest, signal?: AbortSignal): AppThunk<Promise<SuggestionResponse | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.partsSuggestion, { params: data, signal })
    )
  );
};

export const operationsSuggestion = (data: SearchSuggestionRequest, signal?: AbortSignal): AppThunk<Promise<SuggestionResponse | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.operationsSuggestion, { params: data, signal })
    )
  );
};

export const productionBatchesSuggestion = (data: SearchSuggestionRequest, signal?: AbortSignal): AppThunk<Promise<SuggestionResponse | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.productionBatchesSuggestion, { params: data, signal })
    )
  );
};

export const productionOrdersSuggestion = (data: SearchSuggestionRequest, signal?: AbortSignal): AppThunk<Promise<SuggestionResponse | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.productionOrdersSuggestion, { params: data, signal })
    )
  );
};

export const ncFilesSuggestion = (data: SearchSuggestionRequest, signal?: AbortSignal): AppThunk<Promise<SuggestionResponse | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.ncFilesSuggestion, { params: data, signal })
    )
  );
};

export const toolsSuggestion = (data: SearchSuggestionRequest, signal?: AbortSignal): AppThunk<Promise<SuggestionResponse | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.toolsSuggestion, { params: data, signal })
    )
  );
};

export const documentsSuggestion = (data: SearchSuggestionRequest, signal?: AbortSignal): AppThunk<Promise<SuggestionResponse | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.documentsSuggestion, { params: data, signal })
    )
  );
};

export const globalSearch = ({ data, filter, signal }: SearchWithFilterParams<SearchSuggestionRequest>): AppThunk<Promise<GlobalSearchResponse | undefined>> => async (dispatch) => {
  if (filter) {
    data.oDataFilter = filter.oDataFilter;
  }

  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.globalSearch, { params: data, signal })
    )
  );
};

let partsStorage: SearchSuggestionRequest = {};
export const partsSearch = ({ data, filter, select, resetHistory, ignoreHistory }: SearchWithFilterParams<SearchSuggestionRequest>): AppThunk<Promise<PartSearchResponse | undefined>> => async (dispatch) => {
  if (resetHistory) {
    partsStorage = {};
  }

  if (filter) {
    data.oDataFilter = filter.oDataFilter;
  }

  if (filter?.searchFields) {
    data.searchFields = filter.searchFields;
  }

  if (filter?.orderBy) {
    data.orderBy = filter.orderBy;
  }

  if (select) {
    data.select = select;
  }

  const params: SearchSuggestionRequest = {
    ...partsStorage,
    ...data,
  };
  if (!ignoreHistory) {
    partsStorage = params;
  }

  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.partsSearch, { params })
    )
  );
};

export const partStatistic = (params: SearchSuggestionRequest, select?: string[]): AppThunk<Promise<number | undefined>> => async (dispatch) => {
  const data = await dispatch(
    backendCall(
      createApiCall((api) => api.search.partsSearch, {
        params: {
          ...params,
          select: select || [],
          skip: 0,
          top: 1,
        },
      })
    )
  );

  return data?.totalResultCount;
};

export const toolsStatistic = (params: SearchSuggestionRequest): AppThunk<Promise<number | undefined>> => async (dispatch) => {
  const data = await dispatch(
    backendCall(
      createApiCall((api) => api.search.toolsSearch, {
        params: {
          ...params,
          skip: 0,
          top: 1,
        },
      })
    )
  );

  return data?.totalResultCount;
};

export const documentsStatistic = (params: SearchSuggestionRequest): AppThunk<Promise<number | undefined>> => async (dispatch) => {
  const data = await dispatch(
    backendCall(
      createApiCall((api) => api.search.documentsSearch, {
        params: {
          ...params,
          skip: 0,
          top: 1,
        },
      })
    )
  );
  return data?.totalResultCount;
};

export const productionOrdersStatistic = (params: SearchSuggestionRequest, select?: string[]): AppThunk<Promise<number | undefined>> => async (dispatch) => {
  const data = await dispatch(
    backendCall(
      createApiCall((api) => api.search.productionOrdersSearch, {
        params: {
          ...params,
          select: select || [],
          skip: 0,
          top: 1,
        },
      })
    )
  );

  return data?.totalResultCount;
};

export const productionOperationsStatistic = (params: SearchSuggestionRequest, select?: string[]): AppThunk<Promise<number | undefined>> => async (dispatch) => {
  const data = await dispatch(
    backendCall(
      createApiCall((api) => api.search.operationsSearch, {
        params: {
          ...params,
          select: select || [],
          skip: 0,
          top: 1,
        },
      })
    )
  );

  return data?.totalResultCount;
};

let operationsStorage: SearchSuggestionRequest = {};
export const operationsSearch = ({ data, filter, select, resetHistory, signal, ignoreHistory }: SearchWithFilterParams<SearchSuggestionRequest>): AppThunk<Promise<ProductionOperationSearchResponse | undefined>> => async (dispatch) => {
  // Tour demo data: return demo operations search result when registered
  const demoResult = getDemoOperationsSearch();
  if (demoResult) return demoResult;

  if (resetHistory) {
    operationsStorage = {};
  }

  if (filter) {
    data.oDataFilter = filter.oDataFilter;
  }

  if (filter?.searchFields) {
    data.searchFields = filter.searchFields;
  }

  if (filter?.orderBy) {
    data.orderBy = filter.orderBy;
  }

  if (select) {
    data.select = select;
  }

  const params: SearchSuggestionRequest = {
    ...operationsStorage,
    ...data,
  };

  if (!ignoreHistory) {
    operationsStorage = params;
  }

  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.operationsSearch, {
        params,
        ...(signal ? { signal } : {}),
      })
    )
  );
};

let productionBatchesStorage: SearchSuggestionRequest = {};
export const productionBatchesSearch = ({ data, filter, select, resetHistory, signal, ignoreHistory }: SearchWithFilterParams<SearchSuggestionRequest>): AppThunk<Promise<ProductionBatchSearchResponse | undefined>> => async (dispatch) => {
  // Tour demo data: intercept searches targeting a demo batch ID
  const batchIdMatch = filter?.oDataFilter?.match(/ProductionBatchId eq (\d+)/);
  if (batchIdMatch) {
    const demoResult = getDemoBatchSearch(parseInt(batchIdMatch[1]));
    if (demoResult) return demoResult;
  }

  if (resetHistory) {
    productionBatchesStorage = {};
  }

  if (filter) {
    data.oDataFilter = filter.oDataFilter;
  }

  if (filter?.searchFields) {
    data.searchFields = filter.searchFields;
  }

  if (filter?.orderBy) {
    data.orderBy = filter.orderBy;
  }

  if (select) {
    data.select = select;
  }

  if (filter?.search) {
    data.search = filter.search;
  }

  const params: SearchSuggestionRequest = {
    ...productionBatchesStorage,
    ...data,
    facets: ['LinkStatus', 'NcFileStatus'],
  };
  if (!ignoreHistory) {
    productionBatchesStorage = params;
  }

  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.productionBatchesSearch, {
        params,
        ...(signal ? { signal } : {}),
      })
    )
  );
};

export const getProductionBatchStatus = (id: number): AppThunk<Promise<ProductionBatchSearchResponse | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.productionBatchesSearch, {
        params: {
          oDataFilter: `ProductionBatchId eq ${id}`,
        },
      })
    )
  );
};

export const getProductionBatchByNCFile = ({ id, filters, select, search, facets, oDataFilter }: BatchByIdParams): AppThunk<Promise<ProductionBatchSearchResponse | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.productionBatchesSearch, {
        params: {
          oDataFilter: `NcFileId eq ${id}${oDataFilter ? ' and ' + oDataFilter : ''}`,
          orderBy: filters?.orderBy ?? ['EndedOn desc'],
          select,
          ...(facets ? { facets } : {}),
          ...(search ? {} : {}),
          ...(filters?.top ? { top: filters.top } : {}),
          ...(filters?.skip ? { skip: filters.skip } : {}),
        },
      })
    )
  );
};

export const getProductionBatchByProductionOperationId = ({ id, filters, select, search, facets, oDataFilter }: BatchByIdParams): AppThunk<Promise<ProductionBatchSearchResponse | undefined>> => async (dispatch) => {
  // Return empty result if id is undefined or null
  if (id == null) {
    return { searchResult: [], totalResultCount: 0 } as ProductionBatchSearchResponse;
  }

  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.productionBatchesSearch, {
        params: {
          oDataFilter: `Links/any(link: link/ProductionOperationId eq ${id})${oDataFilter ? ' and ' + oDataFilter : ''}`,
          orderBy: filters?.orderBy ?? ['EndedOn desc'],
          select,
          ...(facets ? { facets } : {}),
          ...(search ? { search } : {}),
          ...(filters?.top ? { top: filters.top } : {}),
          ...(filters?.skip ? { skip: filters.skip } : {}),
        },
      })
    )
  );
};

export const getProductionBatchByProductionOrderId = ({ id, filters, select, search, facets, oDataFilter }: BatchByIdParams): AppThunk<Promise<ProductionBatchSearchResponse | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.productionBatchesSearch, {
        params: {
          oDataFilter: `Links/any(link: link/ProductionOrderId eq ${id})${oDataFilter ? ' and ' + oDataFilter : ''}`,
          orderBy: filters?.orderBy ?? ['EndedOn desc'],
          select,
          ...(facets ? { facets } : {}),
          ...(search ? {} : {}),
          ...(filters?.top ? { top: filters.top } : {}),
          ...(filters?.skip ? { skip: filters.skip } : {}),
        },
      })
    )
  );
};

export const getProductionBatchByPart = ({ id, filters, select, search, facets, oDataFilter }: BatchByIdParams): AppThunk<Promise<ProductionBatchSearchResponse | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.productionBatchesSearch, {
        params: {
          oDataFilter: `Links/any(part:part/PartId eq ${id})${oDataFilter ? ' and ' + oDataFilter : ''}`,
          orderBy: ['EndedOn desc'],
          ...(facets ? { facets } : {}),
          ...(select ? { select } : {}),
          ...(search ? {} : {}),
          ...(filters?.top ? { top: filters.top } : {}),
          ...(filters?.skip ? { skip: filters.skip } : {}),
        },
      })
    )
  );
};

export const getProductionBatchByMachineId = ({ machineId, filters, select, search, facets, oDataFilter }: BatchByMachineParams): AppThunk<Promise<ProductionBatchSearchResponse | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.productionBatchesSearch, {
        params: {
          oDataFilter: `MachineId eq ${machineId}${oDataFilter ? ' and ' + oDataFilter : ''}`,
          select,
          ...(search ? { search } : {}),
          ...(facets ? { facets } : {}),
          orderBy: filters?.orderBy ?? ['EndedOn desc'],
          ...(filters?.top ? { top: filters.top } : {}),
          ...(filters?.skip ? { skip: filters.skip } : {}),
        },
      })
    )
  );
};

let productionOrdersStorage: SearchSuggestionRequest = {};
export const productionOrdersSearch = ({ data, filter, select, resetHistory, signal, ignoreHistory }: SearchWithFilterParams<SearchSuggestionRequest>): AppThunk<Promise<ProductionOrderSearchResponse | undefined>> => async (dispatch) => {
  if (resetHistory) {
    productionOrdersStorage = {};
  }

  if (filter) {
    data.oDataFilter = filter.oDataFilter;
  }

  if (filter?.searchFields) {
    data.searchFields = filter.searchFields;
  }

  if (filter?.orderBy) {
    data.orderBy = filter.orderBy;
  }

  if (select) {
    data.select = select;
  }

  const params: SearchSuggestionRequest = {
    ...productionOrdersStorage,
    ...data,
  };
  if (!ignoreHistory) {
    productionOrdersStorage = params;
  }

  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.productionOrdersSearch, {
        params,
        ...(signal ? { signal } : {}),
      })
    )
  );
};

let ncFilesSearchStorage: SearchSuggestionRequest = {};
export const ncFilesSearch = ({ data, filter, select, resetHistory }: SearchWithFilterParams<SearchSuggestionRequest>): AppThunk<Promise<NcFileSearchResponse | undefined>> => async (dispatch) => {
  if (resetHistory) {
    ncFilesSearchStorage = {};
  }

  if (filter) {
    data.oDataFilter = filter.oDataFilter;
  }

  data.orderBy = filter?.orderBy?.length > 0 ? filter?.orderBy : ['CreatedOn desc'];

  if (select) {
    data.select = select;
  }

  const params: SearchSuggestionRequest = {
    ...ncFilesSearchStorage,
    ...data,
    facets: ['NcFileStatus'],
  };
  ncFilesSearchStorage = params;

  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.ncFilesSearch, { params })
    )
  );
};

let toolsSearchStorage: SearchSuggestionRequest = {};
export const toolsSearch = ({ data, filter, select, resetHistory, ignoreHistory }: SearchWithFilterParams<SearchSuggestionRequest>): AppThunk<Promise<ToolSearchResponse | undefined>> => async (dispatch) => {
  if (resetHistory) {
    toolsSearchStorage = {};
  }

  if (filter) {
    data.oDataFilter = filter.oDataFilter;
  }

  if (filter?.orderBy) {
    data.orderBy = filter.orderBy;
  }

  if (filter?.select) {
    data.select = filter.select;
  }

  const params: SearchSuggestionRequest = {
    ...toolsSearchStorage,
    ...data,
  };
  if (!ignoreHistory) {
    toolsSearchStorage = params;
  }

  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.toolsSearch, { params })
    )
  );
};

let documentsSearchStorage: SearchSuggestionRequest = {};
export const documentsSearch = ({ data, filter, select, resetHistory }: SearchWithFilterParams<SearchSuggestionRequest>): AppThunk<Promise<DocumentSearchResponse | undefined>> => async (dispatch) => {
  if (resetHistory) {
    documentsSearchStorage = {};
  }

  if (filter) {
    data.oDataFilter = filter.oDataFilter;
  }

  if (filter?.orderBy) {
    data.orderBy = filter.orderBy;
  }

  if (select) {
    data.select = select;
  }

  const params: SearchSuggestionRequest = {
    ...documentsSearchStorage,
    ...data,
  };

  documentsSearchStorage = params;

  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.documentsSearch, { params })
    )
  );
};

export const getDocumentById = (id: number, signal?: AbortSignal): AppThunk<Promise<Document[] | undefined>> => async (dispatch) => {
  return await dispatch(
    backendCall(
      createApiCall((api) => api.imageHandling.getDocumentById, {
        params: { documentId: id },
        signal,
      })
    )
  );
};

export const recipesSearch = (data: SearchSuggestionRequest, filter?: { oDataFilter?: string }): AppThunk<Promise<RecipeSearchResponse | undefined>> => async (dispatch) => {
  if (filter) {
    data.oDataFilter = filter.oDataFilter;
  }

  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.recipesSearch, { params: data })
    )
  );
};

let recipeOperationsStorage: SearchSuggestionRequest = {};
export const recipeOperationsSearch = ({ data, filter, select, resetHistory, signal, ignoreHistory }: SearchWithFilterParams<SearchSuggestionRequest>): AppThunk<Promise<RecipeOperationSearchResponse | undefined>> => async (dispatch) => {
  if (resetHistory) {
    recipeOperationsStorage = {};
  }

  if (filter) {
    data.oDataFilter = filter.oDataFilter;
  }

  if (filter?.searchFields) {
    data.searchFields = filter.searchFields;
  }

  if (filter?.orderBy) {
    data.orderBy = filter.orderBy;
  }

  if (select) {
    data.select = select;
  }

  const params: SearchSuggestionRequest = {
    ...recipeOperationsStorage,
    ...data,
  };

  if (!ignoreHistory) {
    recipeOperationsStorage = params;
  }

  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.recipeOperationsSearch, {
        params,
        ...(signal ? { signal } : {}),
      })
    )
  );
};

export const machineEventsSearchByProductionBatchId = (filters: { productionBatchId: number; start: string; end: string }, signal?: AbortSignal): AppThunk<Promise<MachineEventSearchResponse | undefined>> => async (dispatch) => {
  const params: SearchSuggestionRequest = {
    oDataFilter: `ProductionBatchId eq ${filters?.productionBatchId} and EndedOn gt ${filters.start} and StartedOn lt ${filters?.end}`,
  };
  return await dispatch(machineEventsSearch(params, signal));
};

export const machineEventsSearch = (params: SearchSuggestionRequest, signal?: AbortSignal): AppThunk<Promise<MachineEventSearchResponse | undefined>> => async (dispatch) => {
  if (!params.oDataFilter) {
    params.oDataFilter = '';
  }

  if (!params?.search) {
    params.search = '*';
  }

  if (!params.orderBy) {
    params.orderBy = ['StartedOn asc'];
  }

  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.machineEvents, { params, signal })
    )
  );
};

export const machineEventCyclesSearch = (params: SearchSuggestionRequest, signal?: AbortSignal): AppThunk<Promise<MachineEventCycleSearchResponse | undefined>> => async (dispatch) => {
  if (!params.oDataFilter) {
    params.oDataFilter = '';
  }

  if (!params?.search) {
    params.search = '*';
  }

  if (!params.orderBy) {
    params.orderBy = ['StartedOn asc'];
  }

  return await dispatch(
    backendCall(
      createApiCall((api) => api.search.machineEventCycles, { params, signal })
    )
  );
};

export const setTotalSearchResult = (resultCount: number | null): AppThunk => (dispatch) => {
  dispatch(setTotalSearchResultAction(resultCount));
};

// === Reducer ===

export default createReducer<SearchState>(JSON.parse(JSON.stringify(initState)), (builder) => {
  builder
    .addCase(clear, () => {
      return initState;
    })
    .addCase(close, () => {
      return initState;
    })
    .addCase(setShouldRefetchDataAction, (state, action) => ({
      ...state,
      shouldRefetchData: action.payload,
    }))
    .addCase(setStatStorage, (state, action) => ({
      ...state,
      statStorage: {
        ...state.statStorage,
        [action.payload.name]: action.payload.value,
      },
    }))
    .addCase(setStatContextStorage, (state, action) => ({
      ...state,
      statContextStorage: action.payload,
    }))
    .addCase(setTotalSearchResultAction, (state, action) => ({
      ...state,
      totalSearchResult: action.payload,
    }));
});
